// src/components/ecommerce/ProductCard.jsx
import React, { useState } from 'react';
import {
  ShoppingCart,
  Heart,
  Eye,
  Package,
  MessageCircle,
  Zap,
  Clock,
  AlertCircle,
  Check,
  Loader2,
  Plus,
  Minus
} from 'lucide-react';
import { getStoreTypeConfig } from './utils/storeTypeConfig';

export const ProductCard = ({
  product,
  bookData,
  storeView = 'grid',
  onClick,
  onAddToCart,
  isInCart = false,
  cartItem = null,
  loading = false,
  styling = {},
  className = ''
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isLiked, setIsLiked] = useState(false);

  const design = bookData?.design || {};
  const storeSettings = bookData?.storeSettings || {};
  const primaryColor = design.primaryColor || '#3b82f6';
  const accentColor = design.accentColor || '#f59e0b';
  const cardRadius = design.cardRadius || '12px';
  const shadowLevel = design.shadowLevel || 'medium';
  const imageFit = design.imageFit || 'cover';
  const imageAspectRatio = design.imageAspectRatio || '4:3';
  const fontSource =
    styling.fontFamily ||
    design.fontFamily ||
    storeSettings.fontFamily ||
    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
  const fontFamily = fontSource.includes(',') ? fontSource : `"${fontSource}", sans-serif`;
  const config = getStoreTypeConfig(storeSettings?.storeType || 'general');

  const getShadowClass = () => {
    switch (shadowLevel) {
      case 'none':
        return '';
      case 'light':
        return 'shadow-sm';
      case 'medium':
        return 'shadow-md';
      case 'strong':
        return 'shadow-lg';
      case 'extra':
        return 'shadow-xl';
      default:
        return 'shadow-md';
    }
  };

  const getAspectRatioClass = () => {
    switch (imageAspectRatio) {
      case '4:3':
        return 'aspect-[4/3]';
      case '3:4':
        return 'aspect-[3/4]';
      case '16:9':
        return 'aspect-video';
      default:
        return 'aspect-square';
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAddToCart?.(product, 1, selectedVariant);
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    onClick?.(product);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    setIsLiked((prev) => !prev);
  };

  const isOutOfStock = product.quantity === 0 || product.status === 'out-of-stock';
  const basePrice = Number(product.price || 0);
  const salePrice = Number(product.salePrice || 0);
  const isOnSale = salePrice > 0 && salePrice < basePrice;
  const discount = isOnSale ? Math.round(((basePrice - salePrice) / basePrice) * 100) : 0;

  const priceToShow = isOnSale ? salePrice : basePrice;

  const cardClasses = storeView === 'masonry' ? `${className} break-inside-avoid mb-4` : className;

  return (
    <div
      className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl ${getShadowClass()} ${cardClasses}`}
      style={{
        borderRadius: cardRadius || '16px',
        fontFamily
      }}
      onClick={handleQuickView}
    >
      <div className={`relative ${getAspectRatioClass()} overflow-hidden bg-gray-100`}>
        {product.images && product.images.length > 0 && !imageError ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="h-full w-full transition-transform duration-500 group-hover:scale-105"
            style={{ objectFit: imageFit }}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-100">
            <Package className="h-12 w-12 text-gray-400" />
          </div>
        )}

        {imageLoading && !imageError && <div className="absolute inset-0 animate-pulse bg-gray-100" />}

        <div className="absolute left-3 top-3 flex flex-col gap-2">
          {isOnSale && (
            <span className="rounded-full px-2.5 py-1 text-xs font-semibold text-white shadow-sm" style={{ backgroundColor: accentColor }}>
              -{discount}%
            </span>
          )}
          {product.featured && (
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
            >
              Featured
            </span>
          )}
          {product.digital && (
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
            >
              <Zap className="mr-1 inline h-3 w-3" />
              Digital
            </span>
          )}
          {isOutOfStock && <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-600">Out of Stock</span>}
        </div>

        <div className="absolute right-3 top-3 flex gap-2 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
          <button
            onClick={handleLike}
            className={`rounded-full p-2 backdrop-blur-sm transition-colors ${
              isLiked ? 'bg-red-100 text-red-600' : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-600'
            }`}
          >
            <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={handleQuickView}
            className="rounded-full bg-white/80 p-2 text-gray-600 backdrop-blur-sm transition-colors hover:bg-white hover:text-gray-900"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        {(product.category || product.brand) && (
          <div className="mb-2 flex items-center gap-2">
            {product.category && (
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-gray-600">
                {product.category}
              </span>
            )}
            {product.category && product.brand && <span className="text-xs text-gray-300">|</span>}
            {product.brand && (
              <span className="text-xs font-semibold" style={{ color: primaryColor }}>
                {product.brand}
              </span>
            )}
          </div>
        )}

        <h3 className="mb-1 line-clamp-2 text-base font-semibold leading-tight text-slate-900 transition-colors group-hover:text-slate-700">
          {product.name}
        </h3>

        {product.description && <p className="mb-3 line-clamp-2 text-sm text-slate-500">{product.description}</p>}

        <div className="mb-3 flex items-end gap-2">
          <span className="text-2xl font-bold leading-none text-slate-900">R{priceToShow.toFixed(2)}</span>
          {isOnSale && <span className="text-sm text-slate-400 line-through">R{basePrice.toFixed(2)}</span>}
          {isOnSale && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
              Save {discount}%
            </span>
          )}
        </div>

        {product.variants && product.variants.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {product.variants.slice(0, 3).map((variant, index) => (
                <button
                  key={variant.id || index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVariant(variant);
                  }}
                  className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                    selectedVariant?.id === variant.id
                      ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {variant.name}
                </button>
              ))}
              {product.variants.length > 3 && (
                <span className="px-2 py-1 text-xs text-gray-500">+{product.variants.length - 3} more</span>
              )}
            </div>
          </div>
        )}

        {isInCart && cartItem && (
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm text-gray-600">In cart:</span>
            <div className="flex items-center rounded border border-gray-300">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="p-1 transition-colors hover:bg-gray-100"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="px-2 text-sm">{cartItem.quantity}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="p-1 transition-colors hover:bg-gray-100"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        <div className="mt-auto pt-1">
          {!config.cartEnabled ? (
            <div className="space-y-2">
              {config.showBookingButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick?.(product);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: styling.primaryColor || primaryColor }}
                >
                  <Clock className="h-4 w-4" />
                  Book Service
                </button>
              )}
              {config.showContactButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <MessageCircle className="h-4 w-4" />
                  Inquire
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock || loading}
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                isOutOfStock
                  ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                  : isInCart
                    ? 'border border-green-300 bg-green-100 text-green-700'
                    : 'text-white hover:opacity-90'
              }`}
              style={{
                backgroundColor: !isOutOfStock && !isInCart ? styling.primaryColor || primaryColor : undefined
              }}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isOutOfStock ? (
                <>
                  <AlertCircle className="h-4 w-4" />
                  Out of Stock
                </>
              ) : isInCart ? (
                <>
                  <Check className="h-4 w-4" />
                  In Cart
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  Add to Cart
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
